import { MGBCollectionMethod, MGBLoadNoteType } from './types';

/** Default payment method for MG-Boomi create (Kenco SIT). */
export const MGB_DEFAULT_COLLECTION_METHOD = MGBCollectionMethod.Prepaid;

/** UI collection-method select options (values mapped to API on submit). */
export const COLLECTION_METHOD_OPTIONS: {
  value: Exclude<MGBCollectionMethod, MGBCollectionMethod.Unset>;
  label: string;
}[] = [
  { value: MGBCollectionMethod.Prepaid, label: 'Prepaid' },
  { value: MGBCollectionMethod.Collect, label: 'Collect' },
  { value: MGBCollectionMethod.ThirdParty, label: 'Third Party' },
];

/** Default commodity weight unit for MG-Boomi (dropdown + API wire format). */
export const MGB_DEFAULT_WEIGHT_UNIT = 'lb';

/** Weight unit options for commodity gross / total weight (MGB TMS supports lb and kg only). */
export const weightUnitsList: string[] = [MGB_DEFAULT_WEIGHT_UNIT, 'kg'];

/** Load note type options — `value` is API wire format; `label` is UI display. */
export const MGB_LOAD_NOTE_TYPE_OPTIONS: {
  value: MGBLoadNoteType;
  label: string;
}[] = [
  { value: MGBLoadNoteType.InternalNote, label: 'Internal Note' },
  {
    value: MGBLoadNoteType.SpecialInstructions,
    label: 'Special Instructions',
  },
];

/** Default commodity pieces unit for MG-Boomi (must match a pallet option value). */
export const MGB_DEFAULT_PIECES_UNIT = 'PALLET';

/**
 * Default Item ID when the user leaves it blank on submit (ENG-7578 / Kenco SIT).
 * Description is required; Item ID is optional and filled with this value for MG.
 */
export const MGB_DEFAULT_ITEM_ID = 'ITEM';

/**
 * Pieces / package-type UOM options from MercuryGate Load Builder
 * (`stops.*.items.*.actualQuantity.unit`). `value` is the ImportWeb wire code.
 */
export const MGB_PIECE_UNIT_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: 'BAG', label: 'Bag(s)' },
  { value: 'BOX', label: 'BOX' },
  { value: 'BDL', label: 'Bundle' },
  { value: 'BUS', label: 'Bushel' },
  { value: 'CTN', label: 'CARTON' },
  { value: 'CAS', label: 'CASE' },
  { value: 'CRT', label: 'Crate(s)' },
  { value: 'DRM', label: 'DRUM(S)' },
  { value: 'EA', label: 'EA' },
  { value: 'FEDEXBOX', label: 'FedEx Box' },
  { value: 'FEDEXENVELOPE', label: 'FedEx Letter' },
  { value: 'FEDEXPAK', label: 'FedEx Pak' },
  { value: 'FEDEXTUBE', label: 'FedEx Tube' },
  { value: 'GAL', label: 'Gallons' },
  { value: '71', label: 'Not Otherwise Specified' },
  { value: 'PKG', label: 'Package' },
  { value: 'PAL', label: 'PAIL(S)' },
  { value: MGB_DEFAULT_PIECES_UNIT, label: 'PALLET' },
  { value: 'PCS', label: 'Piece(s)' },
  { value: 'ROL', label: 'Roll(S)' },
  { value: 'SQFT', label: 'SQ Foot' },
  { value: 'SQYD', label: 'SQ Yard' },
  { value: 'TOT', label: 'TOTE(S)' },
  { value: 'UPS10KGBOX', label: 'UPS 10KG Box' },
  { value: 'UPS25KGBOX', label: 'UPS 25KG Box' },
  { value: 'UPSEXPRESSENVELOPE', label: 'UPS Envelope' },
  { value: 'UPSEXPRESSBOX', label: 'UPS Express Box' },
  { value: 'UPSLETTER', label: 'UPS Letter' },
  { value: 'UPSPAK', label: 'UPS Pak' },
  { value: 'UPSTUBE', label: 'UPS Tube' },
];

export const pieceUnitsList: string[] = MGB_PIECE_UNIT_OPTIONS.map(
  (option) => option.value
);

/** Common NMFC-style freight classes until MG code-table API is wired. */
export const freightClassList: string[] = [
  '50',
  '55',
  '60',
  '65',
  '70',
  '77.5',
  '85',
  '92.5',
  '100',
  '110',
  '125',
  '150',
  '175',
  '200',
  '250',
  '300',
  '400',
  '500',
];

/** Dimension unit options for commodity L × W × H. */
export const dimensionUnitsList: string[] = ['in', 'ft', 'm'];

/**
 * Accessorial codes floated to the top of the dropdown (Grain / Kenco SIT).
 * Remaining options keep API order after these.
 */
export const MGB_PRIORITY_ACCESSORIAL_CODES: string[] = [
  'LGO',
  'LGD',
  'SIG',
  'HAZ',
  'DL',
  'DU',
  'LMP',
  'NC',
  'NS',
  'SAT',
];

/** Filter bogus TMS customer rows from API report data. */
export const MGB_EXCLUDED_CUSTOMER_NAME_PATTERNS = [
  /^credit card$/i,
  /^template$/i,
];

/** Default MG rate mode (Kenco Mode code table). */
export const MGB_DEFAULT_RATE_MODE = 'Truckload';

/**
 * System additional-reference qualifier for Drumkit Quick Quote id sent to MercuryGate.
 * Create mode: user-selectable as DKQN{id}. View mode: read-only.
 */
export const MGB_QUOTE_NUMBER_REF_TYPE = 'Quote Number';

/** Prefixed Drumkit Quote Number format: DKQN{quoteRequestId} (ENG-7570). */
export const MGB_DRUMKIT_QUOTE_NUMBER_PREFIX = 'DKQN';

/** System Rate Min / Rate Max refs (Flash buy band; ENG-7421 / ENG-7570). */
export const MGB_RATE_MIN_REF_TYPE = 'Rate Min';
export const MGB_RATE_MAX_REF_TYPE = 'Rate Max';

/**
 * ImportWeb ReferenceNumber types (ENG-7915). Distinct from the CodeTable
 * names below — MercuryGate rejects `SR:` as a reference type.
 */
export const MGB_CUSTOMER_REP_REF_TYPE = 'Customer Rep';
export const MGB_CARRIER_REP_REF_TYPE = 'Carrier Rep';
export const MGB_TRAILER_LENGTH_REF_TYPE = 'Trailer Length';

/** Boomi CodeTable names for the dedicated special-reference dropdowns. */
export const MGB_CUSTOMER_REP_CODE_TABLE = 'SR:Customer Rep';
export const MGB_CARRIER_REP_CODE_TABLE = 'SR:Carrier Rep';
export const MGB_TRAILER_LENGTH_CODE_TABLE = 'SR:Trailer Length';

/** MG Fuel Surcharge rate qualifiers (RateQualifier wire values). */
export const MGB_FSC_RATE_QUALIFIER = {
  Percent: 'PCT',
  FlatRate: 'FR',
  PerMile: 'PM',
} as const;

export type MGBFscRateQualifier =
  (typeof MGB_FSC_RATE_QUALIFIER)[keyof typeof MGB_FSC_RATE_QUALIFIER];

/** Default FSC qualifier — matches historical percent-only behavior. */
export const MGB_DEFAULT_FSC_RATE_QUALIFIER = MGB_FSC_RATE_QUALIFIER.Percent;

/** FSC qualifier dropdown options (labels match MG Load Builder). */
export const MGB_FSC_RATE_QUALIFIER_OPTIONS: {
  value: MGBFscRateQualifier;
  label: string;
}[] = [
  { value: MGB_FSC_RATE_QUALIFIER.Percent, label: 'Percent' },
  { value: MGB_FSC_RATE_QUALIFIER.FlatRate, label: 'Flat Rate' },
  { value: MGB_FSC_RATE_QUALIFIER.PerMile, label: 'PerMile' },
];

/** DOM id for Rates accessorials editor — Specs jump target (ENG-8147). */
export const MGB_RATES_ACCESSORIALS_SECTION_ID = 'mgb-rates-accessorials';
